<template>
    <div>
        <ColumnsSelector
            ref="sent-requests-columns"
            :fields="fields"
            :active-fields="activeFields"
            @activate="activateColumn"
            @inactivate="inactivateColumn"
        />
        <HamletTable
            ref="table"
            :columns="activeFields"
            :fetch="fetch"
        >
            <template #header-controls>
                <div class="text-right d-flex align-items-center justify-content-end">
                    <ColumnsSelectorButton
                        @click="showColumns"
                    />
                    <slot name="header-controls"/>
                </div>
            </template>

            <template #header-label>
                {{ label }}
            </template>

            <template #cell-messagingStatus="data">
                <div>
                    <b-badge
                        v-if="data.item.messagingStatus !== 'none'"
                        :title="data.item.messagingStatus === 'sent' ? '' : $t('Page(SentRequests):Component(Grid):hover-has-unseen-messages')"
                        :variant="data.item.messagingStatus === 'sent' ? 'success' : 'danger'"
                    >
                        <feather-icon icon="SendIcon" />
                    </b-badge>
                </div>
            </template>

            <template #cell-status="data">
                <StatusBadge
                    type="sent"
                    :status="data.item.status"
                />
            </template>

            <template #cell-deadlineDays="data">
                {{ data.item.daysToDeadLine }}
            </template>

            <template #cell-productId="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-product-id')">{{ getProductId(data.item) }}</div>
            </template>

            <template #cell-logged_in="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-logged-in')">
                    {{ calculateDayDiff(data.item.loggedInAt, data.item) }}
                </div>
            </template>

            <template #cell-opened="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-opened')">
                    {{ calculateDayDiff(data.item.openedAt, data.item) }}
                </div>
            </template>

            <template #cell-changed="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-changed')">
                    {{ calculateDayDiff(data.item.updatedAt, data.item) }}
                </div>
            </template>

            <template #cell-replied="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-replied')">
                    <strong>
                        {{ calculateDayDiff(data.item.repliedAt, data.item) }}
                    </strong>
                </div>
            </template>

            <template #cell-approved="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-approved')">
                    {{ calculateDayDiff(data.item.approvedAt, data.item) }}
                </div>
            </template>

            <template #cell-productName="data">
                {{ getProductName(data.item) }}
            </template>

            <template #cell-sentAt="data">
                {{ renderDateTime(data.item.sentAt) }}
            </template>
        </HamletTable>
    </div>
</template>

<script>
import {BBadge} from 'bootstrap-vue'
import HamletTable from "@core/components/common/HamletTable";
import {ColumnsSelectorButton, ColumnsSelector} from "@core/components/common";
import activeTableColumns from "@core/mixins/active-table-columns";
import StatusBadge from "@/views/request/components/StatusBadge";
import moment from 'moment'
import {getSupplierIdentifier, getSupplierName} from "@/libs/product";
import dateRenderer from "@core/mixins/date-renderer";

export default {
    mixins: [activeTableColumns, dateRenderer],
    components: {HamletTable, ColumnsSelectorButton, ColumnsSelector, BBadge, StatusBadge},
    props: {
        label: {
            default() {
                return this.$t('Page(ForwardedRequest):Component(Label):page-label');
            }
        },
    },
    data() {
        return {
            fields: [
                {
                    key: 'messagingStatus',
                    label: this.$t('Page(SentRequests):Component(Grid):field-messaging-status'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-field-messaging-status'),
                    order: -1,
                    active: true,
                },
                {
                    key: 'status',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-field-status'),
                    order: 0,
                    active: true,
                },
                {
                    key: 'sentAt',
                    label: this.$t('Page(SentRequests):Component(Grid):field-sent-at'),
                    order: 1,
                    active: true,
                },
                {
                    key: 'deadlineDays',
                    label: this.$t('Page(SentRequests):Component(Grid):field-deadline-days'),
                    order: 2,
                    active: true,
                },
                {
                    key: 'productId',
                    label: this.$t('Page(SentRequests):Component(Grid):field-product-id'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-product-id'),
                    order: 3,
                    active: true,
                },
                {
                    key: 'productName',
                    label: this.$t('Page(SentRequests):Component(Grid):field-product-name'),
                    order: 4,
                    active: true,
                },
                {
                    key: 'supplier',
                    label: this.$t('Page(SentRequests):Component(Grid):field-supplier'),
                    order: 5,
                    active: true,
                },
                {
                    key: 'regulations',
                    label: this.$t('Page(SentRequests):Component(Grid):field-regulations'),
                    order: 6,
                    active: false,
                },
                {
                    key: 'level',
                    label: this.$t('Page(SentRequests):Component(Grid):field-level'),
                    order: 7,
                    active: false,
                },
                {
                    key: 'verification',
                    label: this.$t('Page(SentRequests):Component(Grid):field-verification'),
                    order: 8,
                    active: false,
                },
                {
                    key: 'logged_in',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-logged-in'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-logged-in'),
                    order: 9,
                    active: true,
                },
                {
                    key: 'opened',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-opened'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-opened'),
                    order: 10,
                    active: true,
                },
                {
                    key: 'changed',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-changed'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-changed'),
                    order: 11,
                    active: true,
                },
                {
                    key: 'replied',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-replied'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-replied'),
                    order: 12,
                    active: true,
                },
                {
                    key: 'approved',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-approved'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-approved'),
                    order: 13,
                    active: true,
                },
            ],
        };
    },
    methods: {
        ___activeTableUniqueKey(namespace) {
            return this.$router.currentRoute.name + '_' + namespace + '_active_sent_requests';
        },
        calculateDayDiff(date, item) {
            if (!date) {
                return '-';
            }

            return moment(date).diff(item.sentAt, 'days') + 1;
        },
        fetch(config, callback) {
            // callback(this.requests.length, this.requests);
        },
        showColumns() {
            this.$refs['sent-requests-columns'].show();
        },
        renderRequests(requests) {
            this.$refs.table.fetchHandler(requests.length, requests);
        },
        getProductName(sentRequest) {
            return getSupplierName(sentRequest.product);
        },
        getProductId(sentRequest) {
            return getSupplierIdentifier(sentRequest.product);
        },
    },
    created() {
        this.initializeTableColumns(this.fields);
    },
}
</script>
